import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { WEBMAIL_URL } from '@/lib/constants'
import { toast } from 'sonner'
import { CopyButton } from '@/components/shared/CopyButton'
import { StatusBadge } from '@/components/shared/StatusBadge'
import type { Mailbox } from '@/types'
import {
  ArrowLeft, Mail, Key, Settings2, Smartphone, Monitor, Lock,
  Loader2, Download, ExternalLink, RotateCcw, AlertTriangle
} from 'lucide-react'

export function MailboxDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [mailbox, setMailbox] = useState<Mailbox | null>(null)
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState<any>(null)
  const [setupLink, setSetupLink] = useState('')
  const [showPwDialog, setShowPwDialog] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [showSetupDialog, setShowSetupDialog] = useState(false)
  const [downloadingApple, setDownloadingApple] = useState(false)

  const fetchMailbox = async () => {
    if (!id) return
    try {
      const res = await api.get(`/mailboxes/${id}`)
      setMailbox(res.data)
    } catch {
      navigate('/mailboxes')
    } finally {
      setLoading(false)
    }
  }

  const fetchConfig = async () => {
    if (!id) return
    try {
      const res = await api.get(`/mailboxes/${id}/config`)
      setConfig(res.data)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchMailbox()
    fetchConfig()
  }, [id])

  const downloadAppleProfile = async () => {
    if (!id || !mailbox) return
    setDownloadingApple(true)
    try {
      const response = await api.get(`/mailboxes/${id}/config/apple`, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `purpletoad-${mailbox.email.split('@')[0]}.mobileconfig`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch {
      toast.error('Failed to download the Apple Mail profile')
    } finally {
      setDownloadingApple(false)
    }
  }

  const handlePasswordChange = async () => {
    if (!id) return
    if (newPassword.length < 8) {
      // CHECKPOINT: PRD-02 FR-2.5.3 / PRD-10 FR-10.13.5 — Use toast for validation errors
      toast.error('Password must be at least 8 characters.')
      return
    }
    const hasUpper = /[A-Z]/.test(newPassword)
    const hasLower = /[a-z]/.test(newPassword)
    const hasDigit = /\d/.test(newPassword)
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
    if (!hasUpper || !hasLower || !hasDigit || !hasSpecial) {
      // CHECKPOINT: PRD-02 FR-2.5.3 / PRD-10 FR-10.13.5 — Use toast for validation errors
      toast.error('Password must include uppercase, lowercase, digit, and special character.')
      return
    }
    setPwLoading(true)
    try {
      await api.put(`/mailboxes/${id}/password`, { new_password: newPassword })
      setShowPwDialog(false)
      setNewPassword('')
      toast.success('Mailbox password updated')
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      const msg = err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to update password'
      toast.error(msg)
    } finally {
      setPwLoading(false)
    }
  }

  const generateSetupLink = async () => {
    if (!id) return
    try {
      const res = await api.post(`/mailboxes/${id}/setup-link`)
      setSetupLink(res.data.setup_url)
      setShowSetupDialog(true)
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      toast.error(err.response?.data?.detail?.message || 'Failed to generate setup link')
    }
  }

  const handleRestore = async () => {
    if (!id) return
    try {
      await api.post(`/mailboxes/${id}/restore`)
      fetchMailbox()
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      toast.error(err.response?.data?.detail?.message || 'Failed to restore mailbox')
    }
  }

  const handleDelete = async () => {
    if (!id || mailbox?.status === 'deleting' || mailbox?.status === 'deleted') return
    if (!confirm('Delete this mailbox? It will enter a 7-day grace period.')) return
    try {
      await api.delete(`/mailboxes/${id}`)
      toast.success('Mailbox scheduled for deletion')
      navigate('/mailboxes')
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      toast.error(err.response?.data?.detail?.message || 'Failed to delete mailbox')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </div>
    )
  }

  if (!mailbox) return null

  return (
    <div className="space-y-8">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mailboxes')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <p className="pt-eyebrow">Mailbox workspace</p>
          <h1 className="pt-page-title mt-2 truncate">{mailbox.email}</h1>
          <div className="mt-2 flex items-center gap-2">
            <StatusBadge status={mailbox.status} />
            <span className="text-sm text-muted-foreground">
              {mailbox.quota_used_mb} MB / {mailbox.quota_mb} MB
            </span>
          </div>
        </div>
      </div>

      {mailbox.status === 'deleting' && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RotateCcw className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-sm font-medium">This mailbox is scheduled for deletion</p>
                <p className="text-xs text-muted-foreground">Restore it before the grace period ends.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRestore}>
              Restore
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="config" className="space-y-5">
        <TabsList>
          <TabsTrigger value="config">
            <Monitor className="mr-2 h-4 w-4" />
            Client Setup
          </TabsTrigger>
          <TabsTrigger value="security">
            <Lock className="mr-2 h-4 w-4" />
            Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4">
          {/* CHECKPOINT: PRD-10 FR-10.4.3 — Direct client guide, webmail fallback, and Apple profile. */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="border-primary/25 bg-primary/[0.055]">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-violet-400" />
                  Start using this mailbox
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Open webmail or set up a mail app.
                </p>
                <Button asChild size="sm" className="w-full">
                  <Link to={`/client-setup?email=${encodeURIComponent(mailbox.email)}`}>
                    Open Setup Guide
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a href={`${WEBMAIL_URL}/login?email=${encodeURIComponent(mailbox.email)}`}>
                    Open Webmail
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-violet-400" />
                  Apple Mail
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Download a profile for Apple Mail.
                </p>
                <Button variant="outline" size="sm" className="w-full" onClick={downloadAppleProfile} disabled={downloadingApple}>
                  {downloadingApple ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Download Apple profile
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-violet-400" />
                Thunderbird &amp; Other Mail Apps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {config && (
                <div className="grid gap-3 text-sm">
                  <div className="grid min-w-0 gap-1 bg-accent/30 rounded-lg px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                    <span className="text-muted-foreground">Email</span>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 break-all font-mono">{config.email}</span>
                      <CopyButton text={config.email} label="email" />
                    </div>
                  </div>
                  <div className="grid min-w-0 gap-1 bg-accent/30 rounded-lg px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                    <span className="text-muted-foreground">IMAP Server</span>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 break-all font-mono">{config.imap.host}:{config.imap.port} ({config.imap.security})</span>
                      <CopyButton text={`${config.imap.host}:${config.imap.port}`} label="IMAP server" />
                    </div>
                  </div>
                  <div className="grid min-w-0 gap-1 bg-accent/30 rounded-lg px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                    <span className="text-muted-foreground">SMTP Server</span>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 break-all font-mono">{config.smtp.host}:{config.smtp.port} ({config.smtp.security})</span>
                      <CopyButton text={`${config.smtp.host}:${config.smtp.port}`} label="SMTP server" />
                    </div>
                  </div>
                  <div className="grid min-w-0 gap-1 bg-accent/30 rounded-lg px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center">
                    <span className="text-muted-foreground">Username</span>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 break-all font-mono">{config.imap.username}</span>
                      <CopyButton text={config.imap.username} label="username" />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Key className="h-4 w-4 text-violet-400" />
                Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                  Set a new password for this mailbox.
              </p>
              <Button variant="outline" onClick={() => setShowPwDialog(true)} disabled={mailbox.status === 'deleting' || mailbox.status === 'deleted'}>
                Change Password
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-violet-400" />
                Team Member Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                  Create a setup link for the mailbox holder.
              </p>
              <Button variant="outline" onClick={generateSetupLink} disabled={mailbox.status === 'deleting' || mailbox.status === 'deleted'}>
                Generate Setup Link
              </Button>
            </CardContent>
          </Card>

          <Card className="border-destructive/20">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Force Password Reset
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Invalidate the current password and generate a new setup link. The user will need to set a new password.
              </p>
              <Button variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" disabled={mailbox.status === 'deleting' || mailbox.status === 'deleted'} onClick={async () => {
                try {
                  const res = await api.post(`/mailboxes/${id}/force-reset`)
                  toast.success('Password invalidated. Setup link generated.')
                  setSetupLink(res.data.setup_url)
                  setShowSetupDialog(true)
                } catch (err: any) {
                  // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
                  toast.error(err.response?.data?.detail?.message || 'Failed to force reset')
                }
              }}>
                Force Reset & Generate Link
              </Button>
            </CardContent>
          </Card>

          {/* CHECKPOINT: PRD-02 FR-2.3.5 — Mailbox soft delete from detail page. */}
          <Card className="border-red-500/20 bg-red-500/5">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-500">
                <AlertTriangle className="h-4 w-4" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Deletion starts a 7-day grace period. Restore the mailbox before it ends.
              </p>
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={mailbox.status === 'deleting' || mailbox.status === 'deleted'}>
                Delete Mailbox
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Password Dialog */}
      <Dialog open={showPwDialog} onOpenChange={setShowPwDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Mailbox Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              type="password"
              placeholder="New password (min 8 chars, uppercase, lowercase, digit, special)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPwDialog(false)}>Cancel</Button>
            <Button
              onClick={handlePasswordChange}
              disabled={pwLoading || newPassword.length < 8}
            >
              {pwLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Link Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Setup Link Generated</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Share this link with the mailbox holder. It expires after 24 hours.
            </p>
            <div className="flex min-w-0 items-center gap-2 overflow-hidden bg-accent/50 rounded-lg px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-mono">{setupLink}</span>
              <CopyButton text={setupLink} label="setup link" className="shrink-0" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSetupDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
